// Choose a workspace (02-DESIGN/03-console/01-the-console.md § signing in;
// 02-DESIGN/01-identity/03-signing-in.md § sign-up and the platform
// provider): a person signed in under the platform provider with no workspace
// named is placed on the identity plane, whose two verbs are the memberships
// and tenant create. One row per membership — the name, the role, the address
// — and *Create a workspace* where sign-up is allowed, a form in the card's
// place with no address of its own (§ conventions: a form; hq decision 0069):
// Back closes it as *Cancel* does. *Sign out* below. Exactly one membership
// skips the page, unless the person asked for it: the rail's workspace menu
// leads here with the ask — *All workspaces*, and *Create a workspace* with
// the form open — and the page then connects on the identity plane by naming
// `-` as the tenant and lists the one membership beside *Create a workspace*
// (hq decision 0072; ./plane-ask.ts).

import { ChevronRight, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { reservedWhy, tenantPath, validSlug } from "../../address/address.ts";
import { Refusal as BusRefusal, placedTenant, type Who } from "../../platform/bus.ts";
import { refusalSentence } from "../../platform/refusal.ts";
import { consoleAddress, workspaceName } from "../../platform/names.ts";
import { ConnectionProvider, useBus, useConnection, usePage } from "../../platform/session.tsx";
import { endSessionUrl, providerOf } from "../../signin/flow.ts";
import {
  Avatar,
  Button,
  describe,
  Field,
  NotAllowed,
  Pending,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import { planeSubject, PlaneVerb, validDisplayName } from "../../wire/auth.ts";
import {
  SERVICE,
  type CreateTenantReply,
  type CreateTenantRequest,
  type MembershipsReply,
} from "../../wire/identity.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Entry } from "./Entry.tsx";
import { askedPlane, placementOf, skipsChoose } from "./plane-ask.ts";

/** CREATE_FORM names *Create a workspace* in the history entry's state, so the rail's menu can open the page on it. */
export const CREATE_FORM = "create";

export function ChooseWorkspace() {
  const { origin, platform, session, setSession } = usePage();
  const navigate = useNavigate();
  const location = useLocation();
  const lane = session?.lane ?? "oidc";
  const asked = askedPlane(location.state);
  const conn = useBus(origin, platform, null, lane, asked);
  // Sign-out forgets the tokens, then ends the session at the provider where
  // it can (03-signing-in.md § the browser); the provider returns the browser
  // to the sign-in page.
  const signOut = () => {
    const to =
      session === undefined ? undefined : endSessionUrl(origin, providerOf(platform, null, session), session);
    setSession(undefined);
    if (to !== undefined) {
      window.location.assign(to);
      return;
    }
    void navigate("/", { replace: true });
  };
  const { state } = conn;
  if (state.kind === "connecting") {
    return (
      <Entry>
        <p className="faint" aria-busy="true">
          Signing you in…
        </p>
      </Entry>
    );
  }
  if (state.kind === "refused" || state.kind === "ended") {
    const err = state.kind === "refused" ? state.err : new Error(state.reason);
    return (
      <Entry>
        <div className="entry-card">
          <h1>{state.kind === "ended" ? "Session ended" : "Not allowed"}</h1>
          <p className="lead">
            {refusalSentence(state.kind === "refused" ? state.refusal : undefined, undefined) ??
              describe(err)}
          </p>
          <div className="rows">
            <Button kind="primary" size="lg" block onClick={conn.again}>
              Try again
            </Button>
            <Button size="lg" block onClick={signOut}>
              Sign in again
            </Button>
          </div>
        </div>
      </Entry>
    );
  }
  return (
    <ConnectionProvider value={conn}>
      <Choose signOut={signOut} askedPlane={asked} />
    </ConnectionProvider>
  );
}

/** Plane is the identity plane a placement names. */
type Plane = NonNullable<Who["plane"]>;

function Choose({ signOut, askedPlane }: { signOut: () => void; askedPlane: boolean }) {
  const { origin, platform, session, setSession } = usePage();
  const { bus, state } = useConnection();
  const navigate = useNavigate();
  const form = useFormState<typeof CREATE_FORM>();
  const plane = state.kind === "connected" ? state.who.plane : undefined;
  // Where the sign-in landed: the plane; without the ask, exactly one
  // membership placed directly, which the carrier names; or, asked for the
  // plane and placed elsewhere, a refusal that says so (an older auth service).
  const placed =
    state.kind === "connected"
      ? placementOf<Plane>(askedPlane, state.who, placedTenant(state.who))
      : placementOf<Plane>(askedPlane, {}, undefined);
  const [memberships] = useLoad(plane === undefined ? "" : `${plane.lane}/${plane.hash}`, async () => {
    if (placed.kind === "direct") {
      const reply: MembershipsReply = { memberships: placed.memberships };
      return reply;
    }
    if (placed.kind === "refused") {
      throw new BusRefusal(placed.code, placed.message);
    }
    return bus.call<MembershipsReply>(
      SERVICE,
      planeSubject(placed.plane.lane, placed.plane.hash, PlaneVerb.memberships),
      undefined,
    );
  });

  const enter = (slug: string) => {
    if (session === undefined) {
      return;
    }
    setSession({ ...session, slug });
    void navigate(tenantPath(slug), { replace: true });
  };

  const canCreate = platform.sign_up && plane !== undefined;
  const creating = form.open === CREATE_FORM && canCreate;
  // Exactly one membership places the person there directly, with no page in
  // between — unless they asked for this page.
  const only =
    memberships.state === "ready" ? skipsChoose(askedPlane, memberships.value.memberships) : undefined;
  useEffect(() => {
    if (only === undefined || session === undefined) {
      return;
    }
    setSession({ ...session, slug: only.tenant });
    void navigate(tenantPath(only.tenant), { replace: true });
  }, [only, session, setSession, navigate]);
  if (only !== undefined) {
    return (
      <Entry live="placed">
        <p className="faint" aria-busy="true">
          Entering {workspaceName(only.tenant, only.name)}…
        </p>
      </Entry>
    );
  }

  if (creating) {
    return (
      <Entry live="placed">
        <CreateWorkspace plane={plane} onClose={form.close} onCreated={enter} />
      </Entry>
    );
  }
  return (
    <Entry
      live="placed"
      under={
        <button type="button" className="text-link small faint" onClick={signOut}>
          Sign out
        </button>
      }
    >
      <div className="entry-card wide">
        <h1>Choose a workspace</h1>
        <p className="lead">
          You are signed in{session?.display !== undefined ? ` as ${session.display}` : ""}.
        </p>
        {memberships.state === "pending" && <Pending what="Reading your workspaces" />}
        {memberships.state === "refused" && <NotAllowed err={memberships.err} what="Your workspaces" />}
        {memberships.state === "ready" && (
          <div className="rows">
            {memberships.value.memberships.map((m) => (
              <button
                key={m.tenant}
                type="button"
                className="choice-row"
                onClick={() => {
                  enter(m.tenant);
                }}
              >
                <Avatar name={workspaceName(m.tenant, m.name)} square />
                <span className="grow">
                  <span className="cell-main">{workspaceName(m.tenant, m.name)}</span>
                  <span className="cell-sub">
                    {m.role} · {consoleAddress(origin, m.tenant)}
                  </span>
                </span>
                <ChevronRight className="chev" strokeWidth={1.6} aria-hidden="true" />
              </button>
            ))}
            {memberships.value.memberships.length === 0 && (
              <p className="muted">
                You belong to no workspace yet.{canCreate ? "" : " Ask an admin to add you."}
              </p>
            )}
            {canCreate && (
              <button
                type="button"
                className="choice-row dashed"
                onClick={() => {
                  form.show(CREATE_FORM);
                }}
              >
                <span className="avatar square faint" aria-hidden="true">
                  <Plus size={14} strokeWidth={1.8} />
                </span>
                <span className="grow">
                  <span className="cell-main">Create a workspace</span>
                  <span className="cell-sub">For a company, a team, or just you.</span>
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </Entry>
  );
}

function CreateWorkspace({
  plane,
  onClose,
  onCreated,
}: {
  plane: { lane: string; hash: string };
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  const { origin } = usePage();
  const { bus } = useConnection();
  // The name people recognise and the address it lives at: both optional —
  // the address defaults to the person's handle, the name to the address
  // (03-signing-in.md § sign-up).
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const trimmed = address.trim().toLowerCase();
  const trimmedName = name.trim();
  const reserved = reservedWhy(trimmed);
  const valid =
    (trimmed === "" || validSlug(trimmed)) && (trimmedName === "" || validDisplayName(trimmedName));
  const create = () => {
    setBusy(true);
    setErr(undefined);
    const body: CreateTenantRequest = {};
    if (trimmed !== "") {
      body.slug = trimmed;
    }
    if (trimmedName !== "") {
      body.name = trimmedName;
    }
    bus
      .call<CreateTenantReply>(
        SERVICE,
        planeSubject(plane.lane, plane.hash, PlaneVerb.createTenant),
        body,
        30000,
      )
      .then(
        (reply) => {
          onCreated(reply.slug);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  const submit = () => {
    if (!busy && valid) {
      create();
    }
  };
  // The form stands in the card's place, drawn in the entry card's own shape.
  return (
    <div className="entry-card wide">
      <FormPage
        title="Create a workspace"
        line="For a company, a team, or just you. You become its first admin."
        back="Choose a workspace"
        onClose={onClose}
        foot={
          <>
            <Button kind="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button kind="primary" onClick={submit} disabled={busy || !valid}>
              {busy ? "Creating…" : "Create"}
            </Button>
          </>
        }
      >
        {err !== undefined && <NotAllowed err={err} what="Create" />}
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Field label="Name" hint="What people call it. Left empty, its address.">
            <TextInput value={name} onChange={setName} placeholder="Acme" autoFocus />
          </Field>
          <Field
            label="Address"
            hint={
              trimmed === "" || validSlug(trimmed)
                ? `Lowercase letters, digits and hyphens; it becomes ${consoleAddress(origin, trimmed === "" ? "…" : trimmed)}. Left empty, your handle.`
                : reserved !== undefined
                  ? `${reserved}.`
                  : "Lowercase letters, digits and hyphens, beginning with a letter."
            }
          >
            <TextInput value={address} onChange={setAddress} placeholder="your handle" mono />
          </Field>
        </form>
        {busy && <p className="small faint">A moment: the workspace is being set up.</p>}
      </FormPage>
    </div>
  );
}
